import React, { createContext, FC, useState, useRef, useEffect, useContext, useMemo } from "react";
import { FeatureCollection, Geometry } from "geojson";
import { MedicareCountyEnrollment } from "@app/overviews/market-expansion-dashboard/shared/MedicareCountyEnrollment";
import { MarketExpansionFiltersContext } from "@app/overviews/market-expansion-dashboard/contexts/MarketExpansionFiltersContext";
import { MarketExpansionLineOfBusinessEnum } from "@app/overviews/market-expansion-dashboard/shared/MarketExpansionLineOfBusinessEnum";
import { MarketExpansionMapDataContext } from "@app/overviews/market-expansion-dashboard/contexts/MarketExpansionMapDataContext";
import { MARKET_EXPANSION_LINE_OF_BUSINESS_CONFIG } from "@app/overviews/market-expansion-dashboard/shared/marketExpansionLineOfBusinessConfig";
import { cloneDeep } from "lodash";
import { bbox } from "@turf/bbox";
import { LngLatBounds } from "mapbox-gl";
import MarketExpansionService from "@app/overviews/market-expansion-dashboard/marketExpansionService";
import { convertToFeatureCollection } from "@app/overviews/market-expansion-dashboard/shared/marketExpansionMapHelper";
import { MembershipCounty } from "@app/overviews/market-expansion-dashboard/shared/MembershipCounty";
import { MarketplaceCountyPlanCounts } from "@app/overviews/market-expansion-dashboard/shared/MarketplaceCountyPlanCounts";
import { MedicaidCountyPrograms } from "@app/overviews/market-expansion-dashboard/shared/MedicaidCountyPrograms";
import { MarketExpansionCounty } from "@app/overviews/market-expansion-dashboard/shared/MarketExpansionCounty";
import { useDebouncedValue } from "@app/shared/hooks/useDebouncedValue";

const PROGRAM_SELECTION_DEBOUNCE_MS = 1000;

interface MembershipMapDataContextProps {
  countySummaryData: MembershipCounty[];
  membershipShadedCountyData: FeatureCollection<Geometry, MembershipCounty> | undefined;
}

const initialValues: MembershipMapDataContextProps = {
  countySummaryData: [],
  membershipShadedCountyData: undefined,
}

const MembershipMapDataContext = createContext(initialValues);

const MembershipMapDataContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {

  const filtersContext = useContext(MarketExpansionFiltersContext);
  const sharedMapDataContext = useContext(MarketExpansionMapDataContext);
  const marketExpansionService = useRef(new MarketExpansionService()).current;

  const [membershipShadedCountyData, setMembershipShadedCountyData] = useState<FeatureCollection<Geometry, MembershipCounty> | undefined>(initialValues.membershipShadedCountyData);
  const [countySummaryData, setCountySummaryData] = useState<MembershipCounty[]>(initialValues.countySummaryData);

  const isMedicaid = filtersContext.selectedLineOfBusiness === MarketExpansionLineOfBusinessEnum.Medicaid;

  // Debounced so rapid Program checkbox clicks coalesce into a single map refetch instead of one per click. Medicaid specific
  const debouncedSelectedPrograms = useDebouncedValue(filtersContext.selectedPrograms, PROGRAM_SELECTION_DEBOUNCE_MS);
  const programKeysForFetch = isMedicaid ? debouncedSelectedPrograms : filtersContext.selectedPrograms;

  const debouncedProgramsCaughtUp = !isMedicaid || debouncedSelectedPrograms === filtersContext.selectedPrograms;
  const isProgramSelectionReadyForFetch = filtersContext.programSelectionReady && debouncedProgramsCaughtUp;

  useEffect(() => {
    // isCurrent guards against a stale response from a superseded filter selection overwriting state after the fact
    let isCurrent = true;

    if (!filtersContext.selectedState) {
      setMembershipShadedCountyData(convertToFeatureCollection([]));
      setCountySummaryData([]);
    } else {
      buildShadedCountyMapFeatures(() => isCurrent);
    }

    return () => {
      isCurrent = false;
    };
  }, [filtersContext.countyOptions, filtersContext.selectedLineOfBusiness, filtersContext.selectedState, isProgramSelectionReadyForFetch, programKeysForFetch]);

  const buildShadedCountyMapFeatures = (isCurrent: () => boolean) => {
    setMembershipShadedCountyData(undefined);

    let counties = cloneDeep(filtersContext.countyOptions as MembershipCounty[]);
    const state = filtersContext.stateOptions.find(x => x.stateName == filtersContext.selectedState);
    const lineOfBusiness = filtersContext.selectedLineOfBusiness;

    if (!state) {
      setCountySummaryData([]);
      sharedMapDataContext.resetMapData();
      filtersContext.setStateNotFoundError(true);
      return;
    }

    if (!lineOfBusiness) {
      setCountySummaryData([]);
      return;
    }

    const lineOfBusinessConfig = MARKET_EXPANSION_LINE_OF_BUSINESS_CONFIG[lineOfBusiness];
    setCountySummaryData([]);

    lineOfBusinessConfig.fetchCountyMetrics(marketExpansionService, {
      countySSAs: state.counties.map(c => c.countySSA),
      programKeys: programKeysForFetch,
      programSelectionReady: isProgramSelectionReadyForFetch,
      stateCode: state.stateAbbreviation,
    }).then((records: MedicareCountyEnrollment[] | MarketplaceCountyPlanCounts[] | MedicaidCountyPrograms[]) => {
      if (!isCurrent()) return;

      const metricValueBySSA = new Map(
        records.map((r) => [r.countySSA, lineOfBusinessConfig.toMetricValue(r)])
      );

      const medicareRecords = lineOfBusiness === MarketExpansionLineOfBusinessEnum.Medicare ? records as MedicareCountyEnrollment[] : [];
      const marketplaceRecords = lineOfBusiness === MarketExpansionLineOfBusinessEnum.Marketplace ? records as MarketplaceCountyPlanCounts[] : [];
      const medicaidRecords = lineOfBusiness === MarketExpansionLineOfBusinessEnum.Medicaid ? records as MedicaidCountyPrograms[] : [];

      const medicarePayersBySSA = new Map(medicareRecords.map((r) => [r.countySSA, r.payers]));
      const marketplacePayersBySSA = new Map(marketplaceRecords.map((r) => [r.countySSA, r.payers]));
      const metalTiersBySSA = new Map(marketplaceRecords.map((r) => [r.countySSA, r.metalTiers]));
      const medicaidProgramsBySSA = new Map(medicaidRecords.map((r) => [r.countySSA, r.programs]));

      counties = counties.map((county: MarketExpansionCounty) => ({
        ...county,
        marketplacePayers: marketplacePayersBySSA.get(county.countySSA) ?? [],
        medicaidPrograms: medicaidProgramsBySSA.get(county.countySSA) ?? [],
        medicarePayers: medicarePayersBySSA.get(county.countySSA) ?? [],
        metalTiers: metalTiersBySSA.get(county.countySSA) ?? [],
        metricValue: metricValueBySSA.get(county.countySSA) ?? 0,
      }));

      setCountySummaryData(counties);

      const countyFeatures = convertToFeatureCollection(counties);

      if (countyFeatures.features.length) {
        setMembershipShadedCountyData(countyFeatures);
        sharedMapDataContext.setDataLoaded(true);

        const [
          minLongitude,
          minLatitude,
          maxLongitude,
          maxLatitude
        ] = bbox(convertToFeatureCollection(filtersContext.countyOptions));

        sharedMapDataContext.setDataBoundingBox(new LngLatBounds(
          [minLongitude, minLatitude], [maxLongitude, maxLatitude]
        ));
      }
    })
    .catch(() => {
      if (!isCurrent()) return;

      setMembershipShadedCountyData(convertToFeatureCollection([]));
      setCountySummaryData([]);
      sharedMapDataContext.setDataLoaded(true);
    });
  }

  const contextValue: MembershipMapDataContextProps = useMemo(() => ({
    countySummaryData,
    membershipShadedCountyData,
  }), [countySummaryData, membershipShadedCountyData]);

  return (
    <MembershipMapDataContext.Provider value={contextValue}>
      {props.children}
    </MembershipMapDataContext.Provider>
  )
}

export { MembershipMapDataContext, MembershipMapDataContextProvider, MembershipMapDataContextProps };
